// Display helpers — pure functions that convert DB rows into UI-friendly strings.

import type { ConversationRow, MessageExcerpt } from '@/lib/hooks/useConversations';
import type { DecideCardData } from '@/components/standup/DecideCard';
import type { FollowupRowData } from '@/components/standup/FollowupRow';

const MS = { hour: 3_600_000, day: 86_400_000 } as const;

/**
 * Coarse age bucket used for the Standup color-coding dot (polish #22):
 *   fresh  — under 24h unreplied  → green
 *   aging  — 1 to 3 days          → yellow
 *   stale  — over 3 days          → red
 *
 * Separate from the old boolean `warn` so UI components can choose which
 * axis to render — `warn` keeps driving the amber-text age label for threads
 * older than 4h, while `state` drives the new colored status dot.
 */
export type AgeState = 'fresh' | 'aging' | 'stale';

export function ageStateFromMs(diffMs: number): AgeState {
  if (diffMs < MS.day) return 'fresh';
  if (diffMs < 3 * MS.day) return 'aging';
  return 'stale';
}

/**
 * "12m" / "2h" / "3d" style label plus two signals:
 *  - `warn`  — true for anything >= 4h (drives amber age-label text).
 *  - `state` — coarse fresh / aging / stale bucket (drives #22 dot).
 */
export function relativeAge(iso: string): { label: string; warn: boolean; state: AgeState } {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const state = ageStateFromMs(diff);

  if (diff < MS.hour) {
    const mins = Math.max(1, Math.floor(diff / 60_000));
    return { label: `${mins}m`, warn: false, state };
  }
  if (diff < MS.day) {
    const hours = Math.floor(diff / MS.hour);
    return { label: `${hours}h`, warn: hours >= 4, state };
  }
  const days = Math.floor(diff / MS.day);
  return { label: `${days}d`, warn: true, state };
}

/** Extract a reasonable display name from an email address or subject. */
export function displayName(
  customerEmail: string | null,
  senderName: string | null,
  subject: string | null,
): string {
  if (senderName && senderName.trim().length > 0) return senderName.trim();
  if (customerEmail) {
    const local = customerEmail.split('@')[0] ?? customerEmail;
    // Title-case "first.last" → "First Last"
    return local
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  if (subject && subject.trim().length > 0) return subject.trim().slice(0, 40);
  return 'Unknown sender';
}

/** Strip quoted replies and over-long bodies down to a single display line. */
export function excerptFromBody(body: string, max = 160): string {
  if (!body) return '';
  // Cut off at any "On <date>…wrote:" style quote markers Gmail leaves.
  const cut = body.split(/\n(?:On .+? wrote:|-{2,}\s*Original Message|From: )/)[0] ?? body;
  const flat = cut.replace(/\s+/g, ' ').trim();
  return flat.length > max ? flat.slice(0, max).trimEnd() + '…' : flat;
}

/**
 * Per-card intent payload. Pre-computed by the converters so DecideCard and
 * FollowupRow can stay presentational.
 */
export type IntentChip = {
  intent: string;
  label: string;
  chipClass: string;
  evidence: string | null;
} | null;

export function toDecideCardData(
  c: ConversationRow,
  excerpt: MessageExcerpt | undefined,
  aiSummary?: { summary: string; highlight: string | null } | null,
  intentChip?: IntentChip,
): DecideCardData {
  const age = relativeAge(c.last_message_at);
  const name = displayName(c.customer_email, excerpt?.sender_name ?? null, c.subject);
  const subject = (c.subject ?? '').trim();

  return {
    id: c.id,
    brand: c.brand,
    channel: c.channel,
    customerName: name,
    subjectLine: subject.length > 0 ? subject : '',
    excerpt: excerptFromBody(excerpt?.body ?? ''),
    ageLabel: age.label,
    ageWarn: age.warn,
    ageState: age.state,
    address: c.customer_email ?? undefined,
    aiSummary: aiSummary?.summary ?? null,
    aiHighlight: aiSummary?.highlight ?? null,
    intentChip: intentChip ?? null,
  };
}

export function toFollowupRowData(
  c: ConversationRow,
  aiSummary?: { summary: string; highlight: string | null } | null,
  intentChip?: IntentChip,
): FollowupRowData {
  const diffMs = Date.now() - new Date(c.last_message_at).getTime();
  const daysWaiting = Math.max(1, Math.floor(diffMs / MS.day));
  const subject = (c.subject ?? '').trim();
  // Polish #6: the Circle Back row now leads with the Haiku-written ask as
  // its title (shared infra with polish #3), and the secondary line becomes
  // a compact meta string — "Last touched Xd ago" — not a duplicate of the
  // title. Falls back to the raw subject when no AI summary exists yet.
  const title = aiSummary?.summary ?? (subject.length > 0 ? subject : 'Waiting on a reply');
  const meta = `Last touched ${daysWaiting}d ago`;
  const customerName = displayName(c.customer_email, null, c.subject);
  return {
    id: c.id,
    customerName,
    brand: c.brand,
    channel: c.channel,
    daysWaiting,
    ageState: ageStateFromMs(diffMs),
    subject: title,
    summary: meta,
    aiSummary: aiSummary?.summary ?? null,
    aiHighlight: aiSummary?.highlight ?? null,
    intentChip: intentChip ?? null,
  };
}

/** "Friday, April 17 · 8:14 am" */
export function formatStandupTimestamp(d: Date = new Date()): string {
  const date = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const time = d
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
    .toLowerCase()
    .replace(' ', ' ');
  return `${date} · ${time}`;
}
